import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Trophy, Star, Building2, Calendar, ChevronRight, 
  Sparkles, TrendingUp
} from 'lucide-react';

export const TopCandidates = ({ topCandidates, isLoading, onViewCandidate }) => {
  if (isLoading) {
    return (
      <Card data-testid="top-candidates-loading">
        <CardHeader className="pb-3">
          <Skeleton className="h-6 w-36" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} className="h-16" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!topCandidates || topCandidates.length === 0) {
    return (
      <Card data-testid="top-candidates-empty">
        <CardContent className="py-8 text-center text-muted-foreground">
          <Trophy className="h-10 w-10 mx-auto mb-3 opacity-50" strokeWidth={1.5} />
          <p className="text-sm">No top candidates yet</p>
          <p className="text-xs mt-1">Analyze resumes to see top performers</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="top-candidates">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Trophy className="h-4 w-4 text-yellow-500" strokeWidth={1.5} />
            Auto-Shortlist Top 10
          </CardTitle>
          <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20 text-xs">
            <Star className="h-3 w-3 mr-1" />
            Best Matches
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[400px] pr-2">
          <div className="space-y-2">
            {topCandidates.map((candidate, idx) => {
              const parsed = candidate.parsed_data || {};
              const analysis = candidate.analysis || {};
              const isTop3 = idx < 3;
              
              return (
                <div
                  key={candidate.id}
                  className={`
                    group p-3 rounded-lg border transition-all cursor-pointer
                    hover:border-primary/50 hover:shadow-sm
                    ${isTop3 ? 'bg-yellow-500/5 border-yellow-500/20' : 'bg-muted/30'}
                  `}
                  onClick={() => onViewCandidate?.(candidate.id)}
                  data-testid={`top-candidate-${candidate.id}`}
                >
                  <div className="flex items-center gap-3">
                    {/* Rank Badge */}
                    <div className={`
                      flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm
                      ${idx === 0 
                        ? 'bg-yellow-500 text-yellow-950' 
                        : idx === 1 
                        ? 'bg-slate-400 text-slate-950'
                        : idx === 2
                        ? 'bg-amber-600 text-amber-950'
                        : 'bg-muted text-muted-foreground'
                      }
                    `}>
                      {idx + 1}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-sm truncate">
                          {parsed.name || 'Unknown'}
                        </h4>
                        <Badge 
                          className={`text-xs shrink-0 ${
                            candidate.fit_label === 'Strong Fit' 
                              ? 'bg-green-500/10 text-green-500 border-green-500/20'
                              : 'bg-blue-500/10 text-blue-500 border-blue-500/20'
                          }`}
                        >
                          {candidate.fit_label}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        {parsed.experience_years && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" /> {parsed.experience_years} yrs
                          </span>
                        )}
                        {parsed.current_company && (
                          <span className="flex items-center gap-1 truncate">
                            <Building2 className="h-3 w-3" /> {parsed.current_company}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Score */}
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="text-right">
                        <p className="text-lg font-bold text-primary">
                          {Math.round(candidate.final_score || 0)}
                        </p>
                        <p className="text-[10px] text-muted-foreground uppercase">Score</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                  </div>
                  
                  {/* Quick insights for top 3 */}
                  {isTop3 && analysis.green_flags?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-dashed flex items-center gap-2">
                      <Sparkles className="h-3 w-3 text-green-500 shrink-0" />
                      <p className="text-xs text-muted-foreground truncate">
                        {analysis.green_flags.slice(0, 2).join(' • ')}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default TopCandidates;
